import React, {useCallback} from 'react';

import {ServerResponseHandler} from 'src/components/server-response-handler';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import {SORTABLE_DIRECTION, SortableItem, SortableList} from '@pb/kit-component-sortable';


import {AddSection} from './add-section/add-section';
import {EditSectionsRowView} from './edit-sections-row.view';
import {useGetSections} from './hooks/useGetSections';
import {useReorderSections} from './hooks/useReorderSections';

import {sectionsTable, sectionsTableBody, sectionsTableHead} from './sections-table.module.styl';

type Props = {
    conferenceId: number,
    readonly?: boolean,
};

export const Sections = ({conferenceId, readonly}: Props) => {
    const {
        isLoading: isSectionsLoading,
        loadingError: sectionsLoadingError,
        data: sections,
        loadData: loadSections,
    } = useGetSections({conferenceId});

    const {
        busy: isReordering,
        event: onReorder,
    } = useReorderSections({onSuccess: loadSections});

    const reorderSections = useCallback(({itemIds}: {itemIds: string[]}) => {
        onReorder(itemIds.reduce((acc, cur, i) => {
            acc[cur] = ++i;
            return acc;
        }, {} as Record<string, number>));
    }, [onReorder]);

    return (
        <ServerResponseHandler
            smallView={true}
            serverError={sectionsLoadingError}>
            <FormSection headline="Sections">
                <Group className={sectionsTable} direction="v" alignItems="start">
                    <Group className={sectionsTableHead}>
                        <Item style={{width: 225}} className="pl-m">
                            Section Name
                        </Item>
                        <Item style={{width: 125}}>Section Type</Item>
                        <Item style={{width: 215}}>Actions</Item>
                        <Item style={{width: 77}}>Web Version</Item>
                        <Item style={{width: 93}}>Mobile Version</Item>
                    </Group>
                    <Item className={sectionsTableBody}>
                        <BoxLoading loading={isReordering || isSectionsLoading}>
                            <SortableList {...{
                                listName: 'section-list',
                                direction: SORTABLE_DIRECTION.VERTICAL,
                                sortableTheme: 'sections-sortable',
                                handle: '.section__drag-handle',
                                onChange: reorderSections,
                            }}>
                                {sections.map((
                                    {
                                        id,
                                        sectionName,
                                        order,
                                        sectionType,
                                        webVersionEnabled,
                                        mobileVersionEnabled,
                                        iconType,
                                    }) => (
                                    <SortableItem itemId={id.toString()} key={id}>
                                        <EditSectionsRowView
                                            key={id}
                                            section={{
                                                id,
                                                sectionName,
                                                iconType,
                                                order,
                                                sectionType,
                                                webVersionEnabled,
                                                mobileVersionEnabled,
                                                conferenceId,
                                            }}
                                            readonly={readonly}
                                            onUpdate={loadSections}
                                        />
                                    </SortableItem>
                                ))}
                            </SortableList>
                        </BoxLoading>
                    </Item>
                    {!readonly && (
                        <AddSection
                            conferenceId={conferenceId}
                            onAdd={loadSections}
                            sectionsCount={sections.length}
                        />
                    )}
                </Group>
            </FormSection>
        </ServerResponseHandler>
    );
};
